import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutService } from '../../service/app.layout.service';
import { MenuService } from '../services/app.menu.service';
import { Ripple } from 'primeng/ripple';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  /* tslint:disable:component-selector */
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  /* tslint:enable:component-selector */
  templateUrl: './app.menuitem.component.html',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    '[class.layout-root-menuitem]': 'root',
    '[class.active-menuitem]': 'active && !root',
  },
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      state(
        'hidden',
        style({
          display: 'none',
        }),
      ),
      state(
        'visible',
        style({
          display: 'block',
        }),
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
  imports: [Ripple, NgClass, RouterLinkActive, RouterLink, TranslateModule],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  readonly layoutService = inject(LayoutService);
  private readonly cd = inject(ChangeDetectorRef);
  readonly router = inject(Router);
  private readonly menuService = inject(MenuService);

  @Input() public item: any;

  @Input() public index!: number;

  @Input() public root!: boolean;

  @Input() public parentKey!: string;

  public active = false;

  private menuSourceSubscription: Subscription;

  private menuResetSubscription: Subscription;

  public key: string = '';

  constructor() {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
        } else {
          if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
            this.active = false;
          }
        }
      });
    });

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isSlim || this.isHorizontal || this.isCompact) {
        this.active = false;
      } else {
        if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
        }
      }
    });
  }

  public ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

    if (!(this.isSlim || this.isHorizontal || this.isCompact) && this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  public ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }
  }

  public updateActiveStateFromRoute() {
    const activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this.active = true;
      //this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  public itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if ((this.root && this.isSlim) || this.isHorizontal || this.isCompact) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    this.active = !this.active;
    if (this.item.items) {
      if (this.root && this.active && (this.isSlim || this.isHorizontal || this.isCompact)) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.state.staticMenuMobileActive = false;
      }

      if (this.isSlim || this.isHorizontal || this.isCompact) {
        this.menuService.reset();
        this.layoutService.state.menuHoverActive = false;
      }
    }

    this.menuService.onMenuStateChange({ key: this.key });
  }

  public onMouseEnter() {
    // activate item on hover
    if (this.root && (this.isSlim || this.isHorizontal || this.isCompact) && this.layoutService.isDesktop()) {
      if (this.layoutService.state.menuHoverActive) {
        this.active = true;
        this.menuService.onMenuStateChange({ key: this.key });
      }
    }
  }

  public get submenuAnimation() {
    if (
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isCompact())
    )
      return this.active ? 'visible' : 'hidden';
    else return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  public get isHorizontal() {
    return this.layoutService.isHorizontal();
  }

  public get isSlim() {
    return this.layoutService.isSlim();
  }

  public get isCompact() {
    return this.layoutService.isCompact();
  }
}
